/**
 * BonusPieceCapturedPopup Component
 *
 * Displays a special celebratory popup when the bonus piece is captured.
 * Uses distinct gold/yellow styling to emphasize importance.
 * Explains prize wheel eligibility if the capturing player wins.
 * Auto-dismisses after 5 seconds (longer than regular piece destruction popups).
 *
 * Props:
 * - isOpen: Boolean indicating if popup is visible
 * - attackerPlayer: String indicating which player captured the bonus (player1 or player2)
 * - onDismiss: Callback when popup is dismissed
 */

import { useEffect, useRef } from 'react';
import { COPY, getPlayerLabel } from '../copy';

export default function BonusPieceCapturedPopup({
  isOpen = false,
  attackerPlayer = 'player1',
  onDismiss = null,
}) {
  const timeoutRef = useRef(null);

  // Auto-dismiss after 5 seconds (longer than regular pieces for emphasis)
  useEffect(() => {
    if (isOpen) {
      timeoutRef.current = setTimeout(() => {
        if (onDismiss) {
          onDismiss();
        }
      }, 5000);
    }

    // Cleanup timeout on unmount or when isOpen changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, onDismiss]);

  // Handle Escape key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && onDismiss) {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onDismiss]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Determine player label
  const playerLabel = getPlayerLabel(attackerPlayer);

  // Handle manual dismiss
  const handleManualDismiss = () => {
    // Clear auto-dismiss timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div
      data-testid="popup-backdrop"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bonus-captured-title"
      onClick={handleManualDismiss}
    >
      <div
        data-testid="bonus-captured-popup"
        className="bg-dark-charcoal border-2 border-neon-yellow text-neon-yellow rounded-lg p-6 max-w-md w-full mx-4 animate-pulse shadow-glow-yellow shadow-lg"
        onClick={(event) => event.stopPropagation()} // Prevent dismiss when clicking inside popup
      >
        {/* Bonus Piece Icon (Star) */}
        <div className="flex items-center justify-center mb-4">
          <div
            data-testid="bonus-piece-icon"
            className="text-6xl font-bold text-neon-yellow animate-pulse"
          >
            ★
          </div>
        </div>

        {/* Main Title */}
        <h2
          id="bonus-captured-title"
          className="text-2xl font-bold text-center mb-3 text-neon-yellow text-glow-yellow"
        >
          {COPY.bonusCaptured.title}
        </h2>

        {/* Attacker Player */}
        <p className="text-center text-xl text-gray-100 mb-4">
          {COPY.bonusCaptured.message(playerLabel)}
        </p>

        {/* Prize Wheel Eligibility Message */}
        <p className="text-center text-lg text-amber-300 mb-6">
          {COPY.bonusCaptured.eligibility(playerLabel)}
        </p>

        {/* Dismiss Button */}
        <div className="flex justify-center">
          <button
            onClick={handleManualDismiss}
            className="px-6 py-2 border-2 border-white/70 text-neon-yellow bg-dark-purple rounded-lg hover:bg-amber-900 hover:bg-opacity-30 transition-all font-semibold shadow-glow-yellow"
            aria-label="Close popup"
          >
            {COPY.bonusCaptured.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
}
